/* eslint-disable no-inline-comments */
import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withStyles } from '@material-ui/core/styles'
import {
  Paper,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@material-ui/core'
import { Link } from 'react-router-dom'
import { IS_COMPANY_ADMIN } from '../../store/actions/types'
import { showToast } from '../../store/actions/toastAction'
import {
  getActiveLevel,
  getActiveLcSettings,
  setActiveLevel,
  computeValuesForLevel,
  getSettingsAsPoints,
  saveSettingsToDB
} from '../../utils/lc_settings'

const styles = () => ({
  root: {
    padding: 16,
    marginTop: 12
  },
  section: {
    marginTop: 16
  },
  radioRow: {
    display: 'flex',
    flexDirection: 'row'
  },
  table: {
    marginTop: 12
  }
})

class LcSettingsPage extends React.Component {
  state = {
    level: getActiveLevel(),
    preview: computeValuesForLevel(getActiveLevel()),
    capLc: (getActiveLcSettings() && typeof getActiveLcSettings().dailyUserLimitLc === 'number') ? getActiveLcSettings().dailyUserLimitLc : 2,
    saving: false
  }

  handleLevelChange = (e) => {
    const lvl = Number(e.target.value)
    this.setState({ level: lvl, preview: computeValuesForLevel(lvl) })
  }

  handleCapChange = (e) => {
    const v = Math.max(0, Number(e.target.value || 0))
    this.setState({ capLc: v })
  }

  handleSave = async () => {
    const { showToast, userdata } = this.props
    try {
      this.setState({ saving: true })
      // Persist full settings object including daily cap
      const fullSettings = { ...computeValuesForLevel(this.state.level), dailyUserLimitLc: this.state.capLc, level: this.state.level }
      const result = await saveSettingsToDB(fullSettings, { companyID: userdata?.companyID })
      if (result && result.ok) {
        // Live subscription will propagate the change; no local override here
        const scope = result.scope === 'company' ? 'company' : 'platform'
        showToast && showToast({ type: 'SHOW_TOAST', open: true, variant: 'success', message: `LC settings saved (${scope}) and applied live.` })
      } else {
        const errMsg = (result && result.error) ? ` Error: ${result.error}` : ''
        showToast && showToast({ type: 'SHOW_TOAST', open: true, variant: 'error', message: 'Failed to save LC settings to server. No changes were applied.' + errMsg })
      }
    } catch (e) {
      showToast && showToast({ type: 'SHOW_TOAST', open: true, variant: 'error', message: `Error saving LC settings. ${e && e.message ? e.message : 'Please try again.'}` })
    } finally {
      this.setState({ saving: false })
    }
  }

  render() {
    const { classes, userdata } = this.props

    if (!userdata || !IS_COMPANY_ADMIN[userdata.userType]) {
      return (
        <Paper className={classes.root}>
          <Typography variant='h6'>Access restricted</Typography>
          <Typography variant='body2'>Only admins can access LC Settings.</Typography>
        </Paper>
      )
    }

    const lc = this.state.preview
    const pts = getSettingsAsPoints(lc)

    const rows = [
      { label: 'Lead creation', lc: lc.leadCreationLc, pts: pts.leadCreationPoints },
      { label: 'Test creation', lc: lc.testCreationLc, pts: pts.testCreationPoints },
      { label: 'Basic test: LC per correct answer', lc: lc.basicAnswerLc, pts: pts.basicAnswerPoints },
      { label: 'Intermediate test: LC per correct answer', lc: lc.intermediateAnswerLc, pts: pts.intermediateAnswerPoints },
      { label: 'Advanced test: LC per correct answer', lc: lc.advancedAnswerLc, pts: pts.advancedAnswerPoints },
      { label: 'Like given (liker)', lc: lc.likeGivenLc, pts: pts.likeGivenPoints },
      { label: 'Like received (author)', lc: lc.likeReceivedLc, pts: pts.likeReceivedPoints },
      { label: 'Comment given (liker)', lc: lc.commentGivenLc, pts: pts.commentGivenPoints },
      { label: 'Comment received (author)', lc: lc.commentReceivedLc, pts: pts.commentReceivedPoints }
    ]

    return (
      <Paper className={classes.root}>
        {/* <Typography variant='h7'>LC Exchange On: </Typography>
        <input
          type="checkbox"
          checked={this.state.pointsOn}
          onChange={() =>
            this.setState({ pointsOn: !this.state.pointsOn })
          }
        /> */}
        <Typography variant='h6'>LC Distribution Settings</Typography>
        <Typography variant='body2' className={classes.section}>
          Adjust the global LC distribution from Level 1 (lean) to Level 5 (generous). Level 5 reflects current defaults.
        </Typography>

        <div className={classes.section}>
          <Typography variant='subtitle1'>Select Level</Typography>
          <RadioGroup row className={classes.radioRow} value={String(this.state.level)} onChange={this.handleLevelChange}>
            <FormControlLabel value='1' control={<Radio color='primary' />} label='Level 1 (lean)' />
            <FormControlLabel value='2' control={<Radio color='primary' />} label='Level 2' />
            <FormControlLabel value='3' control={<Radio color='primary' />} label='Level 3' />
            <FormControlLabel value='4' control={<Radio color='primary' />} label='Level 4' />
            <FormControlLabel value='5' control={<Radio color='primary' />} label='Level 5 (generous)' />
          </RadioGroup>
        </div>

        <div className={classes.section}>
          <Typography variant='subtitle1'>Daily LC cap per user</Typography>
          <Typography variant='body2' style={{ marginBottom: 8 }}>
            Users stop earning LC for the day after reaching this limit. Default is 2.0 LC.
          </Typography>
          <input
            type='number'
            min='0'
            step='0.1'
            value={this.state.capLc}
            onChange={this.handleCapChange}
            style={{ padding: 8, width: 120 }}
          />
        </div>

        <div className={classes.section}>
          <Typography variant='subtitle1'>Preview</Typography>
          <Table size='small' className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Metric</TableCell>
                <TableCell align='right'>LC</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.label}>
                  <TableCell>{r.label}</TableCell>
                  <TableCell align='right'>{r.lc.toFixed(4)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className={classes.section}>
          <Button color='primary' variant='contained' onClick={this.handleSave} disabled={this.state.saving}>
            {this.state.saving ? 'Saving…' : 'Save'}
          </Button>
          <Button style={{ marginLeft: 8 }} variant='outlined' onClick={() => this.setState({ level: 5, preview: computeValuesForLevel(5) })}>Reset to defaults</Button>
          <Button
            style={{ marginLeft: 8 }}
            variant='outlined'
            color='primary'
            component={Link}
            to='/admin/lc-earnings'
          >
            View LC Earnings Monitor
          </Button>
        </div>
      </Paper>
    )
  }
}

LcSettingsPage.propTypes = {
  classes: PropTypes.object.isRequired,
  userdata: PropTypes.object,
  showToast: PropTypes.func
}

const mapStateToProps = (state) => ({
  userdata: state.userDialog.userDetails[0]
})

const mapDispatchToProps = { showToast }

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(LcSettingsPage))



